import * as Comlink from 'comlink'

import type {
  Activity,
  CsvExportResult,
  DecodedFitFile,
  EditHistoryState,
  EncodeResult,
  ExportFitResult,
  FieldDefsByMesgNum,
  GeoJsonExportResult,
  GpxExportResult,
  Operation,
  OperationResult,
  ReplayResult,
  TcxExportResult,
  ZipFileEntry,
} from '@fit-editor/fit-core'

import type { FitWorkerApi, TerrainSampleResult } from './fit-worker'
import type { TerrainLoadProgress, TerrainLoadResult } from './terrain-loader'

export type { TerrainSampleResult, TerrainLoadProgress, TerrainLoadResult }

/**
 * Thin Comlink client for `fit-worker.ts`. The underlying `Worker` is
 * created lazily on first use, not at module load — this keeps
 * `fit-client.ts` safe to import from code that never actually decodes a
 * file in a given run (e.g. component tests, which mock this module
 * entirely rather than exercising a real `Worker` — jsdom doesn't
 * implement one). See the Frontend Agent's M4 handoff report for the
 * full reasoning.
 */

let workerInstance: Worker | undefined
let remote: Comlink.Remote<FitWorkerApi> | undefined

function getRemote(): Comlink.Remote<FitWorkerApi> {
  if (remote == null) {
    workerInstance = new Worker(new URL('./fit-worker.ts', import.meta.url), { type: 'module' })
    remote = Comlink.wrap<FitWorkerApi>(workerInstance)
  }
  return remote
}

/** Decodes raw FIT bytes off the main thread. Never throws — see `decodeFitFile`'s own contract. */
export async function decodeFitFileAsync(bytes: Uint8Array): Promise<DecodedFitFile> {
  return getRemote().decodeFitFile(Comlink.transfer(bytes, [bytes.buffer]))
}

/** Encodes a `DecodedFitFile` back to bytes off the main thread. */
export async function encodeFitFileAsync(decoded: DecodedFitFile): Promise<EncodeResult> {
  return getRemote().encodeFitFile(decoded)
}

/** Applies a single edit operation off the main thread. */
export async function applyOperationAsync(
  activity: Activity,
  operation: Operation,
): Promise<OperationResult> {
  return getRemote().applyOperation(activity, operation)
}

/** Commits an operation to history (replaying the full log) off the main thread. */
export async function applyToHistoryAsync(
  state: EditHistoryState,
  operation: Operation,
): Promise<EditHistoryState> {
  return getRemote().applyToHistory(state, operation)
}

/** Previews an operation's effect without committing it, off the main thread. */
export async function previewOperationAsync(
  state: EditHistoryState,
  operation: Operation,
): Promise<ReplayResult> {
  return getRemote().previewOperation(state, operation)
}

/** Replays history up to the current cursor off the main thread. */
export async function currentActivityAsync(state: EditHistoryState): Promise<ReplayResult> {
  return getRemote().currentActivity(state)
}

/**
 * Splits an activity at one or more timestamps off the main thread. Not
 * part of the operation-log RPCs above (`splitActivity` produces
 * independent new activities, not a history entry — see
 * `edit/split.ts`'s doc comment in fit-core) but still routed through the
 * worker since it partitions every record/event in a potentially large
 * activity, same main-thread-blocking concern as decode/encode/apply.
 */
export async function splitActivityAsync(
  activity: Activity,
  atTimestamps: Date[],
): Promise<Activity[]> {
  return getRemote().splitActivity(activity, atTimestamps)
}

/**
 * Encodes `activity` to FIT bytes and self-validates the result off the
 * main thread (M7: `export-validation` spec). `fieldDefsByMesgNum` is the
 * value captured at import/decode time (or propagated from a split/merge
 * source) -- see `activity-store.ts`'s `LoadedActivity.fieldDefsByMesgNum`.
 */
export async function exportActivityToFitAsync(
  activity: Activity,
  fieldDefsByMesgNum: FieldDefsByMesgNum,
): Promise<ExportFitResult> {
  return getRemote().exportActivityToFit(activity, fieldDefsByMesgNum)
}

/** Exports to GPX off the main thread (iterates every record). */
export async function exportToGpxAsync(activity: Activity): Promise<GpxExportResult> {
  return getRemote().exportToGpx(activity)
}

/** Exports to TCX off the main thread (iterates every record). */
export async function exportToTcxAsync(activity: Activity): Promise<TcxExportResult> {
  return getRemote().exportToTcx(activity)
}

/** Exports to CSV off the main thread (iterates every record). */
export async function exportToCsvAsync(activity: Activity): Promise<CsvExportResult> {
  return getRemote().exportToCsv(activity)
}

/** Exports to GeoJSON off the main thread (iterates every record). */
export async function exportToGeoJsonAsync(activity: Activity): Promise<GeoJsonExportResult> {
  return getRemote().exportToGeoJson(activity)
}

/**
 * Bundles multiple exported files into one ZIP off the main thread (used
 * by batch export and multi-segment split downloads -- "in addition to",
 * never a replacement for, individual downloads).
 */
export async function bundleFilesToZipAsync(files: ZipFileEntry[]): Promise<Uint8Array> {
  return getRemote().bundleFilesToZip(files)
}

/**
 * Fetches + decodes (in the worker) every Mapterhorn terrain tile the
 * current activity of `history` needs. Network access -- only call this
 * from an explicit, disclosed user action. Rejects with an error whose
 * `name` is `'AbortError'` when cancelled via `cancelTerrainLoadAsync`.
 */
export async function loadTerrainForHistoryAsync(
  requestId: number,
  history: EditHistoryState,
  onProgress: (progress: TerrainLoadProgress) => void,
): Promise<TerrainLoadResult> {
  return getRemote().loadTerrainForHistory(requestId, history, Comlink.proxy(onProgress))
}

/** Aborts an in-flight `loadTerrainForHistoryAsync` call (no-op if it already finished). */
export async function cancelTerrainLoadAsync(requestId: number): Promise<void> {
  return getRemote().cancelTerrainLoad(requestId)
}

/** Samples already-cached terrain for `history`'s current records. Never touches the network. */
export async function sampleTerrainForHistoryAsync(
  history: EditHistoryState,
): Promise<TerrainSampleResult> {
  return getRemote().sampleTerrainForHistory(history)
}

/** Tears down the worker (e.g. on hot-reload or app teardown in tests). */
export function terminateFitWorker(): void {
  workerInstance?.terminate()
  workerInstance = undefined
  remote = undefined
}
